import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { CommonReduxState } from '@src/models/commons/reduxState.interface';
import { resolveElasticDivisionIds } from '../divisions/divisionIdsUtils';
import useHasOrgWideAccess from './useHasOrgWideAccess';

/** The `useGetDivisionsAllowed` result for an entity matrix, narrowed to what the scope needs. */
export interface AllowedDivisionsForScope {
  allowedDivsIds: string[];
  allowedSelectedDivs: string[];
  permissionsLoaded: boolean;
}

export interface ElasticDivisionScope {
  divisionIds: string[];
  /**
   * False while permissions load, or when a division-scoped user holds none of the entity
   * roles: `divisionIds` is `[]` then, which the API reads as the whole organization.
   */
  canQuery: boolean;
}

/**
 * Division ids for the Elastic generic-query API, narrowed to the divisions where the
 * user holds one of the entity roles (same as the Triggers / Event types lists).
 *
 * Pass the `useGetDivisionsAllowed` result for the entity's `DIVISIONS.<ENTITY>.LIST` matrix,
 * so the matrix stays pinned at the call site (see `divisionScoping.guard.test.ts`).
 * Org-wide roles still collapse a full selection to `[]`; division-scoped users always get
 * explicit ids, and no query until they have one.
 */
export const useElasticDivisionScope = ({
  allowedDivsIds,
  allowedSelectedDivs,
  permissionsLoaded,
}: AllowedDivisionsForScope): ElasticDivisionScope => {
  const orgWide = useHasOrgWideAccess();
  const selectedDivsIds = useSelector((state: CommonReduxState) => state?.divisions?.selector?.ids);

  return useMemo(() => {
    const divisionIds = resolveElasticDivisionIds(allowedSelectedDivs, allowedDivsIds, orgWide);
    // `resolveElasticDivisionIds` reads an empty selection as "all". Here the user did pick
    // divisions and none is readable, so expanding to every readable one would show data
    // from divisions they did not choose.
    const pickedOnlyUnreadable = (selectedDivsIds?.length ?? 0) > 0 && allowedSelectedDivs.length === 0;
    return {
      divisionIds,
      canQuery:
        permissionsLoaded && !pickedOnlyUnreadable && (orgWide || divisionIds.length > 0),
    };
  }, [allowedSelectedDivs, allowedDivsIds, orgWide, permissionsLoaded, selectedDivsIds]);
};

/**
 * Division ids for the Elastic generic-query API, derived from the divisions
 * selector. A full selection collapses to `[]` ("all divisions of the org")
 * only for org-wide roles with more than one allowed division; a single
 * reachable division stays explicit. See {@link resolveElasticDivisionIds}.
 */
const useElasticDivisionIds = (): string[] => {
  const selectedDivsIds = useSelector((state: CommonReduxState) => state?.divisions?.selector?.ids);
  const assignedDivisions = useSelector(
    (state: CommonReduxState) => state?.divisions?.assigned?.data?.divisions,
  );

  const allowedDivsIds = useMemo(
    () => Object.values(assignedDivisions ?? {}).map(division => division.id),
    [assignedDivisions],
  );

  const orgWide = useHasOrgWideAccess();

  return useMemo(
    () => resolveElasticDivisionIds(selectedDivsIds ?? [], allowedDivsIds, orgWide),
    [selectedDivsIds, allowedDivsIds, orgWide],
  );
};

export default useElasticDivisionIds;
